import React, { useEffect, useState } from 'react';
import { Badge, Drawer, ScrollArea } from '@mantine/core';

import RequestDetails from './RequestDetails';
import RequestProductInfo from './RequestProductInfo';
import RequestCompetitorDetails from './RequestCompetitorDetails';
import RequestProofOfPricing from './RequestProofOfPricing';
import RequestProductSales from './RequestProductSales';
import PriceChangeRequestModal from './PriceChangeRequestModal';

import StandInRequest from './StanInRequest';
import { PriceChangeRequestModalState } from '../../ts/interfaces/requests';
import { showErrorNotification } from '../../notifications';
import { Loader } from '../common';
import RequestDrawerButtons from './RequestDrawerButtons';
import RequestSapOutcome from './RequestSapOutcome';
import EscalatedRequest from './EscalatedRequest';
import RequestHistory from './RequestHistory';
import InterimPriceInfo from './InterimPriceInfo';
import FinalPriceInfo from './FinalPriceInfo';
import { useRequestDrawer } from '../../context/RequestDrawerContext';
import { SurveySourceBadge } from './SurveySourceBadge';
import { PriceChangeScopeBadge } from './PriceChangeScopeBadge';
import { getPriceChangeScope } from '../../helpers/priceChangeScope';

type Props = {
    onClose?: () => void;
};

function RequestDrawer({ onClose }: Props) {
    const { flatRequest, flatLoading, isOpen, requestId, close } =
        useRequestDrawer();

    const [priceChangeRequestModal, setPriceChangeRequestModal] =
        useState<PriceChangeRequestModalState>({
            opened: false,
            verb: null,
        });

    const openPriceChangeRequestModal = (verb: string) => {
        setPriceChangeRequestModal({
            opened: true,
            verb,
        });
    };

    const closePriceChangeRequestModal = () => {
        setPriceChangeRequestModal({
            opened: false,
            verb: null,
        });
    };

    const handleClose = () => {
        close();
        if (onClose) {
            onClose();
        }
    };

    const getDrawerTitle = () => {
        if (flatLoading) {
            return 'Loading...';
        }
        const baseTitle = `Price Change Request #${flatRequest?.friendlyId}`;
        const showBadgeRow =
            !!flatRequest?.completed ||
            !!flatRequest?.isFromPriceSurvey ||
            !!getPriceChangeScope(flatRequest);

        if (!showBadgeRow) {
            return baseTitle;
        }

        return (
            <span className="flex flex-wrap items-center gap-2">
                {baseTitle}
                <PriceChangeScopeBadge
                    request={flatRequest}
                    size="md"
                    descriptive
                />
                {flatRequest?.isFromPriceSurvey && (
                    <SurveySourceBadge
                        isFromPriceSurvey
                        priceSurveyType={
                            (flatRequest as { priceSurveyType?: string })
                                .priceSurveyType
                        }
                        priceSurveyLabel={
                            (flatRequest as { priceSurveyLabel?: string })
                                .priceSurveyLabel
                        }
                        size="md"
                    />
                )}
                {flatRequest?.completed && (
                    <Badge
                        color={
                            flatRequest.latestStatus === 'Declined'
                                ? 'red'
                                : 'green'
                        }
                        size="lg"
                        variant="light"
                    >
                        {flatRequest.latestStatus === 'Declined'
                            ? 'Declined'
                            : 'Approved'}
                    </Badge>
                )}
            </span>
        );
    };

    useEffect(() => {
        if (!requestId && isOpen) {
            showErrorNotification('Unable to fetch request');
            handleClose();
        }
    }, [requestId, isOpen]);

    return (
        <>
            <Drawer
                title={getDrawerTitle()}
                opened={isOpen}
                onClose={handleClose}
                withCloseButton
                classNames={{
                    title: 'text-primary font-semibold text-lg',
                    content: 'bg-[#f4f5f7]',
                    header: 'bg-[#f4f5f7]',
                    overlay: 'z-[500]',
                    inner: 'z-[600]',
                }}
                size={850}
            >
                {flatLoading && !flatRequest ? (
                    <div className="flex flex-1 h-[calc(100vh-80px)] justify-center items-center">
                        <Loader />
                    </div>
                ) : (
                    <div className="flex flex-col gap-5 ">
                        <RequestDrawerButtons
                            openPriceChangeRequestModal={
                                openPriceChangeRequestModal
                            }
                        />

                        <ScrollArea>
                            <EscalatedRequest requestId={requestId} />
                            <StandInRequest />
                            <RequestProductInfo flatRequest={flatRequest} />
                            <InterimPriceInfo flatRequest={flatRequest} />
                            <FinalPriceInfo flatRequest={flatRequest} />
                            <RequestHistory
                                requestId={requestId}
                                flatRequest={flatRequest}
                            />
                            <RequestSapOutcome requestId={requestId} />
                            <div className="flex flex-col flex-1 shadow-sm bg-white px-5 py-4 rounded-lg mt-5">
                                <RequestDetails flatRequest={flatRequest} />
                                <RequestProductSales
                                    flatRequest={flatRequest}
                                />
                                <RequestCompetitorDetails
                                    requestId={requestId}
                                />
                                <RequestProofOfPricing requestId={requestId} />
                            </div>
                        </ScrollArea>
                    </div>
                )}
            </Drawer>
            <PriceChangeRequestModal
                opened={priceChangeRequestModal.opened}
                verb={priceChangeRequestModal.verb}
                close={closePriceChangeRequestModal}
                closeDrawer={handleClose}
            />
        </>
    );
}

export default RequestDrawer;
